import Decimal from 'decimal.js';
import { v5 as uuidv5 } from 'uuid';
import { Draft } from 'immer';
import _ from 'lodash';
import { ComponentFixture } from '@angular/core/testing';
import { By } from '@angular/platform-browser';
import { DebugElement } from '@angular/core';

import {
  AcceptedPaymentMethod,
  CreditCardProcessingType,
  Diner,
  DinerType,
  PaymentMethodOption,
  PaymentType
} from 'src/app/models';
import { PaymentConstant } from 'src/app/constants';

export class GeneralHelper {
  /** Return the given number as a decimal to number decimal places */
  static asDecimal(num: number | string, decimalNumber = 2): number {
    num = formatCorrectNumber(num);
    const decimal = new Decimal(num || 0);
    return +decimal.toDP(decimalNumber);
  }

  static subtractFrom(minuend: number, subtrahend: number) {
    const difference = Decimal.sub(minuend, subtrahend);
    return +difference.toDP(2);
  }

  /** Sort alpha numerically by property */
  static alphaSortByProperty(property: string, a: any, b: any) {
    return a[property]
      .toString()
      .trim()
      .localeCompare(b[property].toString().trim(), 'en', { numeric: true });
  }

  static generateUuid(prefix: string): string {
    const timestamp = Date.now();
    const name = `${prefix}-${timestamp}-${Math.random()}`;
    return uuidv5(name, uuidv5.URL);
  }

  static updateObject<T>(
    draft: Draft<T>,
    key: string,
    updateKey: string,
    updateValue: Partial<any>
  ): void {
    if (!_.has(draft, key)) {
      draft[key] = {
        [updateKey]: updateValue
      };
    } else if (draft[key][updateKey]) {
      draft[key][updateKey] = updateValue;
    } else {
      (draft as any)[key] = {
        ...draft[key],
        [updateKey]: updateValue
      };
    }
  }
}

function formatCorrectNumber(num: number | string) {
  if (
    !!num &&
    typeof num === 'string' &&
    num.length > 0 &&
    num.charAt(0) === '.'
  ) {
    num = '0' + num;
  }

  return num;
}

export function queryEl<T>(
  fixture: ComponentFixture<T>,
  selector: string
): DebugElement {
  return fixture.debugElement.query(By.css(selector));
}

export function generatePaymentMethodList(params: {
  acceptedPaymentMethod: AcceptedPaymentMethod;
  diner: Diner;
  creditCardFeeEnabled: boolean;
  creditCardProcessingType: CreditCardProcessingType;
}): PaymentMethodOption[] {
  const {
    acceptedPaymentMethod,
    diner,
    creditCardFeeEnabled,
    creditCardProcessingType
  } = params;
  const PAYMENT_METHODS: PaymentMethodOption[] = [];
  const PAYMENT_METHODS_MAP = PaymentConstant.PAYMENT_METHODS_MAP;

  // Check if we should split credit/debit
  // When FALSE: Shows "Credit/Debit" (backward compatible)
  // When TRUE: Shows separate "Credit" and "Debit" options

  Object.keys(PAYMENT_METHODS_MAP).forEach((key) => {
    const PAYMENT_METHOD = PAYMENT_METHODS_MAP[key];
    const isEnabledPaymentMethod: boolean =
      acceptedPaymentMethod[PAYMENT_METHOD.key];

    if (isEnabledPaymentMethod) {
      switch (key) {
        case PaymentType.Credit:
          const shouldSplitCreditDebit =
            creditCardFeeEnabled &&
            creditCardProcessingType !== CreditCardProcessingType.None;
          if (shouldSplitCreditDebit) {
            // Feature ENABLED + Gateway: Add both Credit and Debit as separate options
            PAYMENT_METHODS.push(
              PAYMENT_METHODS_MAP[PaymentType.Credit],
              PAYMENT_METHODS_MAP[PaymentType.Debit]
            );
          } else {
            // Feature DISABLED or No Gateway: Add combined "Credit/Debit" option
            PAYMENT_METHODS.push({
              ...PAYMENT_METHODS_MAP[PaymentType.Credit],
              translationKey: 'payment.creditDebit'
            });
          }
          break;
        case PaymentType.Debit:
          // Skip debit as standalone - it's handled with Credit above
          break;
        case PaymentType.ChargeAccount:
          if (!!diner?.charge_accounts?.length) {
            PAYMENT_METHODS.push(PAYMENT_METHOD);
          }
          break;
        default:
          PAYMENT_METHODS.push(PAYMENT_METHOD);
          break;
      }
    }
  });

  return PAYMENT_METHODS;
}

export function generateSskPaymentMethodList(
  acceptedPaymentMethod: AcceptedPaymentMethod,
  diner?: Diner
): PaymentMethodOption[] {
  const PAYMENT_METHODS: PaymentMethodOption[] = [];

  if (acceptedPaymentMethod.credit_card) {
    PAYMENT_METHODS.push(PaymentConstant.PAYMENT_METHODS_MAP.credit_card);
  }

  if (acceptedPaymentMethod.gift_card) {
    PAYMENT_METHODS.push(PaymentConstant.PAYMENT_METHODS_MAP.gift_card);
  }

  if (diner) {
    const { type, charge_accounts } = diner;

    if (acceptedPaymentMethod.bill_to_room && type === DinerType.Resident) {
      PAYMENT_METHODS.push(PaymentConstant.PAYMENT_METHODS_MAP.bill_to_room);
    }

    if (acceptedPaymentMethod.diner_charge_account && charge_accounts?.length) {
      PAYMENT_METHODS.push(PaymentConstant.PAYMENT_METHODS_MAP.charge_account);
    }
  }

  return PAYMENT_METHODS;
}
